import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { X, Check, Move, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

/**
 * Simple Image Cropper/Positioner
 * Allows users to drag the image to select the visible area
 * Very intuitive - just drag to position!
 */
export function ImageCropper({ 
  imageUrl, 
  onSave, 
  onCancel, 
  aspectRatio = [16, 9],
  language = 'es' 
}) {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  const labels = {
    es: {
      title: 'Ajustar imagen',
      subtitle: 'Arrastra para posicionar la imagen',
      save: 'Guardar',
      cancel: 'Cancelar',
      reset: 'Restablecer',
      zoom: 'Zoom',
      tip: 'Mueve la imagen para centrar la parte más importante'
    },
    en: {
      title: 'Adjust image',
      subtitle: 'Drag to position the image',
      save: 'Save',
      cancel: 'Cancel',
      reset: 'Reset',
      zoom: 'Zoom',
      tip: 'Move the image to center the most important part'
    },
    it: {
      title: 'Regola immagine',
      subtitle: 'Trascina per posizionare l\'immagine',
      save: 'Salva',
      cancel: 'Annulla',
      reset: 'Ripristina',
      zoom: 'Zoom',
      tip: 'Sposta l\'immagine per centrare la parte più importante'
    }
  };

  const t = labels[language] || labels.es;

  // Calculate container dimensions based on aspect ratio
  useEffect(() => {
    const calculateSize = () => {
      const containerWidth = Math.min(500, window.innerWidth - 48);
      const containerHeight = containerWidth * (aspectRatio[1] / aspectRatio[0]);
      setContainerSize({ width: containerWidth, height: containerHeight });
    };
    
    calculateSize();
    // Also recalculate on resize
    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [aspectRatio]);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      // Get current container size or calculate if not ready
      const cWidth = containerSize.width || Math.min(500, window.innerWidth - 48);
      const cHeight = containerSize.height || (cWidth * (aspectRatio[1] / aspectRatio[0]));
      
      if (!containerSize.width) {
        setContainerSize({ width: cWidth, height: cHeight });
      }
      
      // Calculate initial scale to cover the container
      const scaleX = cWidth / naturalWidth;
      const scaleY = cHeight / naturalHeight;
      const initialScale = Math.max(scaleX, scaleY);
      
      setScale(initialScale);
      setImageSize({ width: naturalWidth, height: naturalHeight });
      
      // Center the image initially
      const scaledWidth = naturalWidth * initialScale;
      const scaledHeight = naturalHeight * initialScale;
      setPosition({
        x: (cWidth - scaledWidth) / 2,
        y: (cHeight - scaledHeight) / 2
      });
      
      setImageLoaded(true);
    }
  }, [containerSize, aspectRatio]);

  // Constrain position to keep image covering the container
  const constrainPosition = useCallback((pos, currentScale) => {
    if (!imageSize.width || !containerSize.width) return pos;
    
    const scaledWidth = imageSize.width * currentScale;
    const scaledHeight = imageSize.height * currentScale;
    
    let x = pos.x;
    let y = pos.y;
    
    // Constrain X
    if (scaledWidth >= containerSize.width) {
      x = Math.min(0, Math.max(containerSize.width - scaledWidth, x));
    } else {
      x = (containerSize.width - scaledWidth) / 2;
    }
    
    // Constrain Y
    if (scaledHeight >= containerSize.height) {
      y = Math.min(0, Math.max(containerSize.height - scaledHeight, y));
    } else {
      y = (containerSize.height - scaledHeight) / 2;
    }
    
    return { x, y };
  }, [imageSize, containerSize]);

  // Mouse/Touch handlers
  const handleStart = useCallback((clientX, clientY) => {
    setIsDragging(true);
    setStartPos({ x: clientX - position.x, y: clientY - position.y });
  }, [position]);

  const handleMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    
    const newPos = {
      x: clientX - startPos.x,
      y: clientY - startPos.y
    };
    
    setPosition(constrainPosition(newPos, scale));
  }, [isDragging, startPos, scale, constrainPosition]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Zoom controls
  const handleZoomIn = () => {
    const newScale = Math.min(scale * 1.2, 5);
    setScale(newScale);
    setPosition(constrainPosition(position, newScale));
  };

  const handleZoomOut = () => {
    // Don't zoom out beyond cover
    const minScaleX = containerSize.width / imageSize.width;
    const minScaleY = containerSize.height / imageSize.height;
    const minScale = Math.max(minScaleX, minScaleY);
    const newScale = Math.max(scale / 1.2, minScale);
    setScale(newScale);
    setPosition(constrainPosition(position, newScale));
  };

  const handleReset = () => {
    const scaleX = containerSize.width / imageSize.width;
    const scaleY = containerSize.height / imageSize.height;
    const initialScale = Math.max(scaleX, scaleY);
    setScale(initialScale);
    
    const scaledWidth = imageSize.width * initialScale;
    const scaledHeight = imageSize.height * initialScale;
    setPosition({
      x: (containerSize.width - scaledWidth) / 2,
      y: (containerSize.height - scaledHeight) / 2
    });
  };

  // Save the cropped image
  const handleSave = async () => {
    if (!imageRef.current || !containerSize.width) return;

    try {
      // Create a canvas to crop the image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Output size (reasonable quality)
      const outputWidth = Math.min(1200, containerSize.width * 2);
      const outputHeight = outputWidth * (aspectRatio[1] / aspectRatio[0]);
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      // Calculate source coordinates
      const scaleRatio = outputWidth / containerSize.width;
      const sourceX = -position.x / scale;
      const sourceY = -position.y / scale;
      const sourceWidth = containerSize.width / scale;
      const sourceHeight = containerSize.height / scale;
      
      // Draw the cropped portion
      ctx.drawImage(
        imageRef.current,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      );
      
      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create a file from the blob
          const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
          onSave(file, canvas.toDataURL('image/jpeg', 0.9));
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error cropping image:', error);
      // Fallback: just save the original
      onSave(null, imageUrl);
    }
  };

  // Render via portal to escape any overflow:hidden or transform containers
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4">
      {/* Emergency close button in case something goes wrong */}
      <button 
        onClick={onCancel}
        className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-colors"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>
      
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b">
          <h3 className="font-semibold text-lg text-center">{t.title}</h3>
          <p className="text-sm text-gray-500 text-center mt-1">{t.subtitle}</p>
        </div>

        {/* Image Container */}
        <div className="p-4 bg-gray-100">
          <div
            ref={containerRef}
            className="relative overflow-hidden rounded-xl bg-gray-200 mx-auto cursor-move"
            style={{ 
              width: containerSize.width, 
              height: containerSize.height,
              touchAction: 'none'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Image */}
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Crop preview"
              crossOrigin="anonymous"
              onLoad={handleImageLoad}
              onError={(e) => {
                console.error('Failed to load image for cropping');
                // Try loading without crossOrigin
                if (e.target.crossOrigin) {
                  e.target.crossOrigin = null;
                  e.target.src = imageUrl;
                }
              }}
              className="absolute select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: 'top left',
                maxWidth: 'none',
                pointerEvents: 'none'
              }}
              draggable={false}
            />

            {/* Drag indicator overlay */}
            {imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={`bg-black/30 rounded-full p-3 transition-opacity ${isDragging ? 'opacity-0' : 'opacity-70'}`}>
                  <Move className="w-6 h-6 text-white" />
                </div>
              </div>
            )}

            {/* Loading state */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full" />
              </div>
            )}
          </div>

          {/* Tip */}
          <p className="text-xs text-gray-500 text-center mt-3">
            <Move className="w-3 h-3 inline mr-1" />
            {t.tip}
          </p>
        </div>

        {/* Zoom Controls */}
        <div className="px-4 py-3 bg-gray-50 flex items-center justify-center gap-2">
          <span className="text-sm text-gray-500 mr-2">{t.zoom}:</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            className="h-9 w-9 p-0"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <div className="w-16 text-center text-sm text-gray-600">
            {Math.round(scale * 100)}%
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleZoomIn}
            className="h-9 w-9 p-0"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="h-9 w-9 p-0 ml-2"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            <X className="w-4 h-4 mr-2" />
            {t.cancel}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="flex-1 bg-gold hover:bg-gold/90 text-white"
            style={{ backgroundColor: '#D4A574' }}
            disabled={!imageLoaded}
          >
            <Check className="w-4 h-4 mr-2" />
            {t.save}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ImageCropper;
